import { logger } from '@/core/logger';
import { useCallback, useState } from 'react';
import {
  TOP_UP_TYPE,
  updateTopUpType,
} from '../services/topUpService';

export type CompletePaymentMethod = 'bank-transfer' | 'proof-of-mortgage';
export type CompletePaymentStep =
  | 'method'
  | 'bank-transfer'
  | 'submission-received';

function messageFromUnknownError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Pay Now dialog stack:
 * method picker → bank-transfer details → submit proof,
 * or method picker → submit proof (Proof of Mortgage).
 */
export function useCompletePaymentFlow() {
  const [step, setStep] = useState<CompletePaymentStep | null>(null);
  const [method, setMethod] = useState<CompletePaymentMethod | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const open = useCallback(() => {
    setMethod(null);
    setErrorMessage(null);
    setStep('method');
  }, []);

  const close = useCallback(() => {
    setStep(null);
    setMethod(null);
    setIsUpdating(false);
    setErrorMessage(null);
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const continueFromMethod = useCallback(async () => {
    if (isUpdating || method === null) {
      return;
    }

    if (method === 'bank-transfer') {
      setStep('bank-transfer');
      return;
    }

    if (method === 'proof-of-mortgage') {
      setIsUpdating(true);
      setErrorMessage(null);
      try {
        await updateTopUpType(TOP_UP_TYPE.MORTGAGE);
        setStep('submission-received');
      } catch (error) {
        logger.error('useCompletePaymentFlow: updateTopUpType mortgage failed', {
          error,
        });
        setErrorMessage(messageFromUnknownError(error));
      } finally {
        setIsUpdating(false);
      }
    }
  }, [isUpdating, method]);

  const backToMethod = useCallback(() => {
    if (isUpdating) {
      return;
    }
    setStep('method');
  }, [isUpdating]);

  const confirmTransfer = useCallback(async () => {
    if (isUpdating) {
      return;
    }
    setIsUpdating(true);
    setErrorMessage(null);
    try {
      await updateTopUpType(TOP_UP_TYPE.BANK_TRANSFER);
      setStep('submission-received');
    } catch (error) {
      logger.error('useCompletePaymentFlow: updateTopUpType bank transfer failed', {
        error,
      });
      setErrorMessage(messageFromUnknownError(error));
    } finally {
      setIsUpdating(false);
    }
  }, [isUpdating]);

  return {
    method,
    setMethod,
    isMethodVisible: step === 'method',
    isBankTransferVisible: step === 'bank-transfer',
    isSubmissionReceivedVisible: step === 'submission-received',
    canContinue: method !== null && !isUpdating,
    isUpdating,
    errorMessage,
    clearError,
    open,
    close,
    continueFromMethod,
    backToMethod,
    confirmTransfer,
  };
}
